// ─────────────────────────────────────────────────────────────────────────────
// OarlockGattInspector — Connect/disconnect button + GATT tree viewer.
//
// Lives inside the per-vendor `OarlockDeviceCard`. While UUIDs are unknown,
// this UI is the user-facing GATT discovery tool: the rower pairs the device
// once via the browser BLE chooser, taps Discover, and the resulting service /
// characteristic tree is rendered with a "Copy report" button so they can
// paste it back to the developer.
// ─────────────────────────────────────────────────────────────────────────────

import { useMemo, useRef, useState } from 'react';
import { Bluetooth, BluetoothConnected, BluetoothOff, Copy, Download, Info, Radio, Search, Square, Zap, Eye, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import useOarlockBleConnection from '../hooks/useOarlockBleConnection';
import { NK_SERVICES, NK_CHARS } from '../services/oarlock/drivers/NKEmpowerDriver';

function PropertiesBadge({ props }) {
  const flags = Object.entries(props).filter(([, v]) => v).map(([k]) => k);
  if (flags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {flags.map(f => (
        <span key={f} className="px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300">
          {f}
        </span>
      ))}
    </div>
  );
}

/**
 * Cheap multi-format byte preview: int16-LE / uint16-LE / float32-LE arrays.
 * Helpful when reverse-engineering an unknown frame — one of these decodings
 * usually "looks right" (smooth force curve, plausible angle range).
 */
function decodeBytesPreview(dv) {
  if (!dv || dv.byteLength === 0) return '';
  const parts = [];
  // int16-LE
  if (dv.byteLength >= 2) {
    const arr = [];
    for (let i = 0; i + 1 < dv.byteLength && arr.length < 8; i += 2) arr.push(dv.getInt16(i, true));
    parts.push(`i16LE: [${arr.join(', ')}${dv.byteLength > 16 ? ', …' : ''}]`);
  }
  // uint16-LE
  if (dv.byteLength >= 2) {
    const arr = [];
    for (let i = 0; i + 1 < dv.byteLength && arr.length < 8; i += 2) arr.push(dv.getUint16(i, true));
    parts.push(`u16LE: [${arr.join(', ')}${dv.byteLength > 16 ? ', …' : ''}]`);
  }
  // float32-LE
  if (dv.byteLength >= 4) {
    const arr = [];
    for (let i = 0; i + 3 < dv.byteLength && arr.length < 4; i += 4) {
      const v = dv.getFloat32(i, true);
      arr.push(Number.isFinite(v) ? v.toFixed(2) : 'NaN');
    }
    parts.push(`f32LE: [${arr.join(', ')}${dv.byteLength > 16 ? ', …' : ''}]`);
  }
  return parts.join(' · ');
}

function buildReport(vendor, deviceName, tree) {
  const lines = [];
  lines.push(`# CrewZ GATT Discovery — ${vendor.toUpperCase()}`);
  lines.push(`Device: ${deviceName || '(unnamed)'}`);
  lines.push(`Captured: ${new Date(tree.capturedAt).toISOString()}`);
  lines.push('');
  for (const svc of tree.services) {
    lines.push(`Service ${svc.uuid}${svc.isPrimary ? ' (primary)' : ''}`);
    if (svc.characteristicsError) {
      lines.push(`  ERROR: ${svc.characteristicsError}`);
      continue;
    }
    for (const ch of svc.characteristics) {
      const props = Object.entries(ch.properties).filter(([, v]) => v).map(([k]) => k).join(', ');
      lines.push(`  Char ${ch.uuid}  [${props}]`);
    }
    lines.push('');
  }
  return lines.join('\n');
}

export default function OarlockGattInspector({ vendor, vendorLabel, rigBySide }) {
  const { status, supported, busy, lastError, connect, disconnect, discover, readDeviceInfo, startLiveCapture, readChar, probeWake, writeConfigBytes, setRigBySide } = useOarlockBleConnection(vendor);
  const [tree, setTree] = useState(null);
  const [deviceInfo, setDeviceInfoState] = useState(null);
  const [captureRows, setCaptureRows] = useState([]);
  const [capturing, setCapturing] = useState(false);
  const [subStatus, setSubStatus] = useState([]); // [{ key, uuid, ok, error? }]
  const [probeAttempts, setProbeAttempts] = useState([]); // [{ payload, ok, error? }]
  const [oneShotReads, setOneShotReads] = useState({}); // { configRead: { hex, length }, dataConfig: {...} }
  const [customHex, setCustomHex] = useState(''); // free-form hex payload
  const [customWriteLog, setCustomWriteLog] = useState([]); // [{ ts, hex, ok, error?, withoutResponse }]
  const [fuzzState, setFuzzState] = useState(null); // { running, current, total, hex, winners: [], log: [] }
  const stopCaptureRef = useRef(null);
  const fuzzAbortRef = useRef(false);
  // Live row count for closures inside the fuzzer (state captured at call time
  // would always read the initial value).
  const captureRowsCountRef = useRef(0);
  captureRowsCountRef.current = captureRows.length;

  // Keep driver's rigBySide in sync with the current form state so ZF/ZA
  // corrections are applied when streaming eventually starts.
  // (No-op while UUIDs unknown but the wiring is in place.)
  useMemo(() => {
    setRigBySide(rigBySide);
    return null;
  }, [rigBySide, setRigBySide]);

  const onConnect = async (opts = {}) => {
    const r = await connect(opts);
    if (r.ok) {
      toast.success(`Connected to ${r.info?.name || vendorLabel}`);
    } else {
      // User-cancelled chooser is a NotFoundError — show a helpful follow-up
      // for the most common cause: OS-level pairing has claimed the device.
      const isCancel = /NotFoundError|User cancelled/i.test(r.error || '');
      if (isCancel) {
        toast('Oarlock not in the list?', {
          description: 'If your phone\'s Bluetooth settings already show it as connected, unpair it there (or turn Bluetooth off/on) and try again.',
          duration: 8000,
        });
      } else {
        toast.error(`Connect failed: ${r.error}`);
      }
    }
  };

  const onConnectAcceptAll = () => onConnect({ acceptAllDevices: true });

  const onDisconnect = async () => {
    await disconnect();
    setTree(null);
    toast.success('Disconnected');
  };

  const onDiscover = async () => {
    const r = await discover();
    if (r.ok) {
      setTree(r.tree);
      const totalChars = r.tree.services.reduce((acc, s) => acc + s.characteristics.length, 0);
      toast.success(`Discovered ${r.tree.services.length} service${r.tree.services.length === 1 ? '' : 's'} · ${totalChars} characteristic${totalChars === 1 ? '' : 's'}`);
    } else {
      toast.error(`Discover failed: ${r.error}`);
    }
  };

  const onCopyReport = async () => {
    if (!tree) return;
    const text = buildReport(vendor, status.deviceName, tree);
    try {
      await navigator.clipboard.writeText(text);
      toast.success('GATT report copied to clipboard');
    } catch (_e) {
      toast.error('Clipboard not available — long-press the report to copy.');
    }
  };

  const onReadDeviceInfo = async () => {
    const r = await readDeviceInfo();
    if (r?.ok) {
      setDeviceInfoState(r.info);
      toast.success('Device info read');
    } else {
      toast.error(`Device info failed: ${r?.error || 'unknown'}`);
    }
  };

  const onStartCapture = async () => {
    setCaptureRows([]);
    setSubStatus([]);
    const result = await startLiveCapture((frame) => {
      setCaptureRows(prev => [
        { ...frame, idx: prev.length + 1 },
        ...prev.slice(0, 199),    // keep last 200 frames
      ]);
    });
    // New driver API returns { stop, status }. Fall back to old shape for safety.
    const stop = typeof result === 'function' ? result : result?.stop;
    const status = Array.isArray(result?.status) ? result.status : [];
    setSubStatus(status);
    if (stop) {
      stopCaptureRef.current = stop;
      setCapturing(true);
      const okCount = status.filter(s => s.ok).length;
      const totalCount = status.length || 4;
      if (okCount === 0) {
        toast.error('Live capture started but NO channels subscribed — see status below');
      } else if (okCount < totalCount) {
        toast(`Live capture started — ${okCount}/${totalCount} channels subscribed`, { duration: 5000 });
      } else {
        toast.success(`Live capture started — all ${okCount} channels subscribed. Pull a stroke.`);
      }
    } else {
      toast.error('Could not start live capture');
    }
  };

  const onStopCapture = () => {
    if (typeof stopCaptureRef.current === 'function') {
      try { stopCaptureRef.current(); } catch (_e) { /* noop */ }
    }
    stopCaptureRef.current = null;
    setCapturing(false);
  };

  const onProbeWake = async () => {
    const r = await probeWake();
    if (!r?.ok) {
      toast.error(`Wake probe failed: ${r?.error || 'unknown'}`);
      return;
    }
    setProbeAttempts(r.attempts || []);
    const ok = (r.attempts || []).filter(a => a.ok).length;
    if (ok === 0) {
      toast.error('All wake-probe writes rejected by device');
    } else {
      toast.success(`Sent ${ok} wake probe${ok === 1 ? '' : 's'} — watch for frames below`);
    }
  };

  const onReadConfigRead = async () => {
    const r = await readChar(NK_SERVICES.config, NK_CHARS.configRead);
    if (!r.ok) { toast.error(`configRead failed: ${r.error}`); return; }
    setOneShotReads(prev => ({ ...prev, configRead: { hex: r.hex, length: r.length } }));
    toast.success(`configRead → ${r.length}B`);
  };

  const onReadDataConfig = async () => {
    const r = await readChar(NK_SERVICES.data, NK_CHARS.dataConfig);
    if (!r.ok) { toast.error(`dataConfig failed: ${r.error}`); return; }
    setOneShotReads(prev => ({ ...prev, dataConfig: { hex: r.hex, length: r.length } }));
    toast.success(`dataConfig → ${r.length}B`);
  };

  // Parse a free-form hex string ("a5 01 00 00", "A5,01,00,00", "0xA5 0x01"…)
  // into a Uint8Array. Returns null on invalid input.
  const parseHexInput = (raw) => {
    if (!raw) return null;
    const cleaned = raw.replace(/0x/gi, '').replace(/[\s,;:]+/g, '');
    if (cleaned.length === 0 || cleaned.length % 2 !== 0) return null;
    if (!/^[0-9a-f]+$/i.test(cleaned)) return null;
    const out = new Uint8Array(cleaned.length / 2);
    for (let i = 0; i < cleaned.length; i += 2) {
      out[i / 2] = parseInt(cleaned.slice(i, i + 2), 16);
    }
    return out;
  };

  const onCustomWrite = async (withoutResponse) => {
    const bytes = parseHexInput(customHex);
    if (!bytes) {
      toast.error('Invalid hex — use pairs like "a5 01 00 00" or "0xA5,0x01"');
      return;
    }
    if (bytes.length > 20) {
      toast.error('Payload too long (max 20 bytes for safety)');
      return;
    }
    const r = await writeConfigBytes(bytes, { withoutResponse });
    const entry = {
      ts: Date.now(),
      hex: Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join(' '),
      ok: !!r.ok,
      error: r.error,
      withoutResponse,
    };
    setCustomWriteLog(prev => [entry, ...prev.slice(0, 19)]);
    if (r.ok) {
      toast.success(`Wrote ${bytes.length}B${withoutResponse ? ' (no-response)' : ''}`);
    } else {
      toast.error(`Write rejected: ${r.error}`);
    }
  };

  // Decode preview helper for one-shot read results — u16-LE, u32-LE, i16-LE.
  const decodeReadPreview = (hex) => {
    if (!hex) return '';
    const bytes = hex.split(/\s+/).filter(Boolean).map(h => parseInt(h, 16));
    if (bytes.length < 2) return '';
    const u16 = [];
    for (let i = 0; i + 1 < bytes.length && u16.length < 16; i += 2) {
      const v = bytes[i] | (bytes[i + 1] << 8);
      if (v !== 0xFFFF) u16.push(v); else break; // stop at sentinel
    }
    return `u16LE (until 0xFFFF): [${u16.join(', ')}]${u16.length === 16 ? ' …' : ''}`;
  };

  // ── Auto-fuzzer ──────────────────────────────────────────────────────────
  // Walks through a curated set of candidate "start streaming" payloads while
  // Live Capture is running. After each write, waits `WAIT_MS` and checks
  // whether the capture row count grew; if it did, that payload is recorded
  // as a "winner". Tries both write modes (with/without response).
  const FUZZ_WAIT_MS = 700;
  const FUZZ_CANDIDATES = [
    // Single-byte enables
    [0x01], [0x02], [0x05], [0x0a], [0x10], [0x20], [0x55], [0xa5], [0xaa], [0xff],
    // Common NK / generic sync headers
    [0xa5, 0x5a], [0x5a, 0xa5], [0xaa, 0x55], [0x55, 0xaa],
    [0xa5, 0x01], [0xa5, 0x02], [0xa5, 0x55, 0x01, 0x00],
    [0xa5, 0x5a, 0xa5, 0x5a],
    // Generic "start" packets
    [0x01, 0x00, 0x00, 0x00], [0x02, 0x01, 0x00], [0x03, 0x01, 0x01],
    [0x01, 0x05], [0x01, 0x08], [0x01, 0x0b],     // enable field 5/8/11
    [0x01, 0x05, 0x01],                            // enable + ack pattern
    // Echo of configRead prefix
    [0x05, 0x00, 0x08, 0x00, 0x1c, 0x00, 0x2c, 0x00],
    // Long "session start" guesses
    [0x80, 0x01], [0x80, 0x01, 0x01], [0x80, 0x01, 0x00, 0x00],
    [0xc0, 0x01], [0xe0, 0x01],
    // Two-byte commands often used by NK competitors
    [0x10, 0x01], [0x11, 0x01], [0x12, 0x01], [0x20, 0x01],
  ];

  const onAutoFuzz = async () => {
    if (!capturing) {
      toast.error('Start Live Capture first so we can detect responses');
      return;
    }
    fuzzAbortRef.current = false;
    setFuzzState({ running: true, current: 0, total: FUZZ_CANDIDATES.length * 2, hex: '', winners: [], log: [] });

    let idx = 0;
    for (const candidate of FUZZ_CANDIDATES) {
      for (const noResp of [false, true]) {
        if (fuzzAbortRef.current) break;
        idx += 1;
        const bytes = new Uint8Array(candidate);
        const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join(' ');
        const before = captureRowsCountRef.current;
        setFuzzState(prev => prev ? { ...prev, current: idx, hex: `${hex}${noResp ? ' (NR)' : ''}` } : prev);
        try {
          await writeConfigBytes(bytes, { withoutResponse: noResp });
        } catch (_e) {
          // ignore individual write errors so the loop continues
        }
        // Wait for any reply
        await new Promise(r => setTimeout(r, FUZZ_WAIT_MS));
        const after = captureRowsCountRef.current;
        const gained = after - before;
        const entry = { hex, noResp, gained };
        setFuzzState(prev => {
          if (!prev) return prev;
          const log = [entry, ...prev.log.slice(0, 49)];
          const winners = gained > 0 ? [entry, ...prev.winners] : prev.winners;
          return { ...prev, log, winners };
        });
      }
      if (fuzzAbortRef.current) break;
    }
    setFuzzState(prev => prev ? { ...prev, running: false } : prev);
    toast.success('Auto-fuzz complete — see results below');
  };

  const onAbortFuzz = () => {
    fuzzAbortRef.current = true;
  };

  const buildCaptureText = () => [
    `# CrewZ Live Capture — ${vendor.toUpperCase()}`,
    `Device: ${status.deviceName || '(unnamed)'}`,
    `Captured: ${new Date().toISOString()}`,
    `Frames: ${captureRows.length} (most recent first)`,
    '',
    'idx | t_ms | char_key | uuid | hex_bytes',
    '----|------|----------|------|----------',
    ...[...captureRows].reverse().map(r => `${r.idx} | ${r.ts} | ${r.key} | ${r.uuid} | ${r.hex}`),
  ].join('\n');

  const onCopyCapture = async () => {
    if (captureRows.length === 0) return;
    try {
      await navigator.clipboard.writeText(buildCaptureText());
      toast.success(`Copied ${captureRows.length} frames`);
    } catch (_e) {
      toast.error('Clipboard not available');
    }
  };

  /**
   * Save the capture as a .txt file in the device Downloads folder. Uses a
   * Blob + temporary <a download> so it works in Chrome on Android (where
   * clipboards are unreliable across tabs / when the page is backgrounded).
   */
  const onDownloadCapture = () => {
    if (captureRows.length === 0) return;
    try {
      const text = buildCaptureText();
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `crewz-${vendor}-capture-${stamp}.txt`;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Free the blob URL on the next tick so the download has a chance to start.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Saved ${captureRows.length} frames to Downloads`);
    } catch (e) {
      toast.error(`Download failed: ${e?.message || e}`);
    }
  };

  if (!supported) {
    return (
      <div className="mt-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 text-[11px] text-amber-700 dark:text-amber-300 flex items-start gap-2" data-testid={`${vendor}-ble-unsupported`}>
        <BluetoothOff className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div>
          <p className="font-bold">Web Bluetooth not available in this browser.</p>
          <p className="mt-0.5">Use Chrome / Edge on Android or desktop. On iOS, install <strong>Bluefy</strong> to test pairing.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-3" data-testid={`${vendor}-ble-inspector`}>
      <div className="flex items-center justify-between gap-2 p-3 rounded-lg bg-zinc-50 dark:bg-zinc-900/40 border border-zinc-200 dark:border-zinc-700">
        <div className="flex items-center gap-2 min-w-0">
          {status.connected ? (
            <BluetoothConnected className="w-4 h-4 text-emerald-500 shrink-0" />
          ) : (
            <Bluetooth className="w-4 h-4 text-zinc-400 shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-xs font-bold text-zinc-900 dark:text-white truncate">
              {status.connected
                ? `Connected · ${status.deviceName || vendorLabel}`
                : `${vendorLabel} not connected`}
            </p>
            <p className="text-[10px] text-zinc-500 dark:text-zinc-400">
              {status.stream?.configured
                ? 'Streaming UUIDs configured.'
                : 'Discovery mode — pair once, then capture the GATT tree.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {status.connected ? (
            <>
              <button
                type="button"
                onClick={onDiscover}
                disabled={busy}
                data-testid={`${vendor}-ble-discover-btn`}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 text-[11px] font-bold uppercase tracking-wider hover:opacity-90 transition disabled:opacity-50"
              >
                <Search className="w-3 h-3" />
                {busy ? 'Scanning…' : 'Discover'}
              </button>
              <button
                type="button"
                onClick={onDisconnect}
                disabled={busy}
                data-testid={`${vendor}-ble-disconnect-btn`}
                className="px-2.5 py-1.5 rounded-md bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 text-[11px] font-bold uppercase tracking-wider hover:bg-rose-100 dark:hover:bg-rose-900/50 transition disabled:opacity-50"
              >
                Disconnect
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => onConnect()}
              disabled={busy}
              data-testid={`${vendor}-ble-connect-btn`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-600 text-white text-[11px] font-bold uppercase tracking-wider hover:bg-blue-700 transition disabled:opacity-50"
            >
              <Bluetooth className="w-3 h-3" />
              {busy ? 'Pairing…' : 'Connect'}
            </button>
          )}
        </div>
      </div>

      {/* Fallback: if the filtered chooser doesn't surface the oarlock, let the
          rower scan with no filter. Common when firmware advertises an unusual
          name or the device was just woken from sleep. */}
      {!status.connected && (
        <div className="px-3 py-2 rounded-lg bg-zinc-50 dark:bg-zinc-900/40 border border-zinc-200 dark:border-zinc-700 text-[10px] text-zinc-600 dark:text-zinc-300 flex items-start gap-2" data-testid={`${vendor}-ble-fallback`}>
          <Bluetooth className="w-3 h-3 mt-0.5 shrink-0 text-zinc-500" />
          <div className="flex-1 leading-relaxed">
            <p className="font-bold uppercase tracking-wider text-zinc-700 dark:text-zinc-200">Not in the list?</p>
            <p className="mt-0.5">
              If your phone&apos;s Bluetooth already shows the oarlock as connected, <strong>unpair it there</strong> (or toggle Bluetooth off/on) — the OS holds the link so the browser can&apos;t see it.
            </p>
          </div>
          <button
            type="button"
            onClick={onConnectAcceptAll}
            disabled={busy}
            data-testid={`${vendor}-ble-connect-all-btn`}
            className="shrink-0 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 hover:opacity-90 transition disabled:opacity-50"
          >
            Show all
          </button>
        </div>
      )}

      {lastError && !/NotFoundError|User cancelled/i.test(lastError) && (
        <div className="px-3 py-2 rounded-lg bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 text-[11px] text-rose-700 dark:text-rose-300" data-testid={`${vendor}-ble-error`}>
          {lastError}
        </div>
      )}

      {/* Device Info read panel (visible only while connected). */}
      {status.connected && (
        <div className="rounded-lg border-2 border-zinc-200 dark:border-zinc-700 overflow-hidden" data-testid={`${vendor}-ble-deviceinfo-panel`}>
          <div className="px-3 py-2 flex items-center justify-between bg-zinc-50 dark:bg-zinc-900/40 border-b border-zinc-200 dark:border-zinc-700">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-700 dark:text-zinc-200">
              <Info className="w-3 h-3" />
              Device Info
            </div>
            <button
              type="button"
              onClick={onReadDeviceInfo}
              disabled={busy}
              data-testid={`${vendor}-ble-deviceinfo-btn`}
              className="px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition disabled:opacity-50"
            >
              {busy ? 'Reading…' : 'Read'}
            </button>
          </div>
          {deviceInfo && (
            <div className="p-3 space-y-1 text-[11px] font-mono bg-white dark:bg-zinc-800" data-testid={`${vendor}-ble-deviceinfo-values`}>
              {Object.entries(deviceInfo).map(([k, v]) => (
                <div key={k} className="flex">
                  <span className="w-28 text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">{k}</span>
                  <span className="text-zinc-900 dark:text-white break-all">{String(v)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Live Byte Logger — subscribes to all 4 notifiable chars and streams raw
          notifications. Used to identify which char is force / angle / summary
          and reverse-engineer the byte layout. */}
      {status.connected && (
        <div className="rounded-lg border-2 border-zinc-200 dark:border-zinc-700 overflow-hidden" data-testid={`${vendor}-ble-capture-panel`}>
          <div className="px-3 py-2 flex items-center justify-between bg-zinc-50 dark:bg-zinc-900/40 border-b border-zinc-200 dark:border-zinc-700">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-zinc-700 dark:text-zinc-200">
              <Radio className={`w-3 h-3 ${capturing ? 'text-rose-500 animate-pulse' : ''}`} />
              Live Byte Capture
              {capturing && <span className="ml-1 text-rose-500 normal-case">· {captureRows.length} frames</span>}
            </div>
            <div className="flex items-center gap-1">
              {capturing ? (
                <button
                  type="button"
                  onClick={onStopCapture}
                  data-testid={`${vendor}-ble-capture-stop-btn`}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/50 transition"
                >
                  <Square className="w-3 h-3" />
                  Stop
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onStartCapture}
                  disabled={busy}
                  data-testid={`${vendor}-ble-capture-start-btn`}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 hover:opacity-90 transition disabled:opacity-50"
                >
                  <Radio className="w-3 h-3" />
                  Start
                </button>
              )}
              <button
                type="button"
                onClick={onCopyCapture}
                disabled={captureRows.length === 0}
                data-testid={`${vendor}-ble-capture-copy-btn`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition disabled:opacity-30"
              >
                <Copy className="w-3 h-3" />
                Copy
              </button>
              <button
                type="button"
                onClick={onDownloadCapture}
                disabled={captureRows.length === 0}
                data-testid={`${vendor}-ble-capture-download-btn`}
                title="Save capture as .txt to Downloads"
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition disabled:opacity-30"
              >
                <Download className="w-3 h-3" />
                Download
              </button>
            </div>
          </div>
          {/* Diagnostic row — sub channel status + wake probe + read-once buttons */}
          {capturing && subStatus.length > 0 && (
            <div className="px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800" data-testid={`${vendor}-ble-sub-status`}>
              <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300 mb-1">
                Subscription status
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
                {subStatus.map((s) => (
                  <div key={s.key} className="flex items-center gap-1 text-[10px] font-mono">
                    {s.ok
                      ? <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                      : <XCircle className="w-3 h-3 text-rose-500 shrink-0" />}
                    <span className={s.ok ? 'text-zinc-700 dark:text-zinc-200' : 'text-rose-700 dark:text-rose-300'}>
                      {s.key}
                    </span>
                    {!s.ok && s.error && (
                      <span className="text-rose-600 dark:text-rose-400 truncate">· {s.error}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Diagnostic toolbar — wake probe + one-shot reads. Always visible
              when connected so the rower can poke a sleeping oarlock awake. */}
          <div className="px-3 py-2 flex items-center flex-wrap gap-1.5 bg-zinc-50 dark:bg-zinc-900/30 border-b border-zinc-200 dark:border-zinc-700">
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300 mr-1">
              Diagnostics:
            </span>
            <button
              type="button"
              onClick={onProbeWake}
              disabled={busy}
              data-testid={`${vendor}-ble-wake-btn`}
              title="Write candidate wake bytes to the config-write char to provoke streaming"
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-white hover:bg-amber-600 transition disabled:opacity-50"
            >
              <Zap className="w-3 h-3" />
              {busy ? 'Waking…' : 'Wake / Probe'}
            </button>
            <button
              type="button"
              onClick={onReadConfigRead}
              disabled={busy}
              data-testid={`${vendor}-ble-read-configread-btn`}
              title={`One-shot READ of ${NK_CHARS.configRead}`}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition disabled:opacity-30"
            >
              <Eye className="w-3 h-3" />
              Read configRead
            </button>
            <button
              type="button"
              onClick={onReadDataConfig}
              disabled={busy}
              data-testid={`${vendor}-ble-read-dataconfig-btn`}
              title={`One-shot READ of ${NK_CHARS.dataConfig}`}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition disabled:opacity-30"
            >
              <Eye className="w-3 h-3" />
              Read dataConfig
            </button>
            {fuzzState?.running ? (
              <button
                type="button"
                onClick={onAbortFuzz}
                data-testid={`${vendor}-ble-fuzz-stop-btn`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/50 transition"
              >
                <Square className="w-3 h-3" />
                Stop fuzz ({fuzzState.current}/{fuzzState.total})
              </button>
            ) : (
              <button
                type="button"
                onClick={onAutoFuzz}
                disabled={busy || !capturing}
                data-testid={`${vendor}-ble-fuzz-btn`}
                title={!capturing ? 'Start Live Capture first' : 'Cycle through ~30 candidate start-streaming commands'}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider bg-purple-600 text-white hover:bg-purple-700 transition disabled:opacity-30"
              >
                <Zap className="w-3 h-3" />
                Auto-Fuzz
              </button>
            )}
          </div>
          {/* Fuzzer progress / winners */}
          {fuzzState && (
            <div className="px-3 py-1.5 bg-purple-50 dark:bg-purple-900/20 border-b border-purple-200 dark:border-purple-800 text-[10px] font-mono space-y-1" data-testid={`${vendor}-ble-fuzz-results`}>
              <div className="flex items-center justify-between">
                <span className="font-bold uppercase tracking-wider text-purple-700 dark:text-purple-300">
                  {fuzzState.running ? `Fuzzing… ${fuzzState.current}/${fuzzState.total}` : 'Fuzz complete'}
                </span>
                {fuzzState.running && <span className="text-purple-600 dark:text-purple-400">Now: {fuzzState.hex}</span>}
              </div>
              {fuzzState.winners.length > 0 ? (
                <div className="space-y-0.5">
                  <div className="font-bold text-emerald-700 dark:text-emerald-400">
                    🎯 WINNERS — payloads that triggered frames:
                  </div>
                  {fuzzState.winners.map((w, i) => (
                    <div key={`w-${i}`} className="flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                      <CheckCircle2 className="w-3 h-3 shrink-0" />
                      <span>{w.hex}{w.noResp ? ' (no-resp)' : ''} → +{w.gained} frames</span>
                    </div>
                  ))}
                </div>
              ) : (
                !fuzzState.running && <div className="text-zinc-500 dark:text-zinc-400">No payload provoked a notification. Try OS-level pairing or a SpeedCoach next to it.</div>
              )}
            </div>
          )}
          {/* Wake-probe attempt summary */}
          {probeAttempts.length > 0 && (
            <div className="px-3 py-1.5 bg-zinc-50 dark:bg-zinc-900/20 border-b border-zinc-200 dark:border-zinc-700 text-[10px] font-mono" data-testid={`${vendor}-ble-wake-attempts`}>
              <div className="font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-0.5">Wake probe attempts:</div>
              {probeAttempts.map((a, i) => (
                <div key={i} className="flex items-center gap-1">
                  {a.ok
                    ? <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                    : <XCircle className="w-3 h-3 text-rose-500 shrink-0" />}
                  <span className="text-zinc-700 dark:text-zinc-200">write {a.payload}</span>
                  {!a.ok && a.error && <span className="text-rose-600 dark:text-rose-400 truncate">· {a.error}</span>}
                </div>
              ))}
            </div>
          )}
          {/* One-shot read results */}
          {(oneShotReads.configRead || oneShotReads.dataConfig) && (
            <div className="px-3 py-1.5 bg-zinc-50 dark:bg-zinc-900/20 border-b border-zinc-200 dark:border-zinc-700 text-[10px] font-mono space-y-1" data-testid={`${vendor}-ble-read-results`}>
              <div className="font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-0.5">Read results:</div>
              {oneShotReads.configRead && (
                <div className="space-y-0.5">
                  <div className="break-all">
                    <span className="text-zinc-500 dark:text-zinc-400">configRead ({oneShotReads.configRead.length}B):</span>{' '}
                    <span className="text-zinc-900 dark:text-white">{oneShotReads.configRead.hex || '(empty)'}</span>
                  </div>
                  <div className="text-emerald-700 dark:text-emerald-400">
                    {decodeReadPreview(oneShotReads.configRead.hex)}
                  </div>
                </div>
              )}
              {oneShotReads.dataConfig && (
                <div className="space-y-0.5">
                  <div className="break-all">
                    <span className="text-zinc-500 dark:text-zinc-400">dataConfig ({oneShotReads.dataConfig.length}B):</span>{' '}
                    <span className="text-zinc-900 dark:text-white">{oneShotReads.dataConfig.hex || '(empty)'}</span>
                  </div>
                  <div className="text-emerald-700 dark:text-emerald-400">
                    {decodeReadPreview(oneShotReads.dataConfig.hex)}
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Custom Hex Write — free-form payload tester. The wake probe only
              fires 4 canned bytes; this lets the rower paste any candidate
              start-streaming command (e.g. echo of configRead, NK proto guesses)
              and see whether the device responds with a notification. */}
          <div className="px-3 py-2 bg-zinc-50 dark:bg-zinc-900/30 border-b border-zinc-200 dark:border-zinc-700 space-y-1.5" data-testid={`${vendor}-ble-custom-write`}>
            <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 dark:text-zinc-300">
              Custom Hex Write → configWrite (85920200-…)
            </div>
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={customHex}
                onChange={(e) => setCustomHex(e.target.value)}
                placeholder="e.g. a5 01 00 00 — pairs of hex bytes"
                spellCheck={false}
                autoComplete="off"
                data-testid={`${vendor}-ble-custom-hex-input`}
                className="flex-1 min-w-0 px-2 py-1 rounded text-[10px] font-mono bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-600 text-zinc-900 dark:text-white"
              />
              <button
                type="button"
                onClick={() => onCustomWrite(false)}
                disabled={busy || !customHex.trim()}
                data-testid={`${vendor}-ble-custom-write-btn`}
                title="writeValueWithResponse"
                className="shrink-0 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 hover:opacity-90 transition disabled:opacity-50"
              >
                Write
              </button>
              <button
                type="button"
                onClick={() => onCustomWrite(true)}
                disabled={busy || !customHex.trim()}
                data-testid={`${vendor}-ble-custom-write-nr-btn`}
                title="writeValueWithoutResponse"
                className="shrink-0 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-white hover:bg-amber-600 transition disabled:opacity-50"
              >
                Write (No-Resp)
              </button>
            </div>
            <div className="text-[9px] text-zinc-500 dark:text-zinc-400 leading-snug">
              Tip: start Live Capture first, then write here while watching for new frames. Try echoing
              the configRead bytes (`05 00 08 00 1c 00 …`), or candidate NK headers like `a5 5a`,
              `01 02 03 04`, etc.
            </div>
            {customWriteLog.length > 0 && (
              <div className="mt-1 max-h-32 overflow-y-auto divide-y divide-zinc-200 dark:divide-zinc-700 text-[10px] font-mono" data-testid={`${vendor}-ble-custom-write-log`}>
                {customWriteLog.map((row) => (
                  <div key={row.ts} className="py-0.5 flex items-start gap-1">
                    {row.ok
                      ? <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0 mt-0.5" />
                      : <XCircle className="w-3 h-3 text-rose-500 shrink-0 mt-0.5" />}
                    <div className="min-w-0">
                      <div className="text-zinc-900 dark:text-white break-all">{row.hex}</div>
                      {row.withoutResponse && <span className="text-amber-600 dark:text-amber-400 text-[9px]">no-resp</span>}
                      {!row.ok && <div className="text-rose-600 dark:text-rose-400 text-[9px] break-all">{row.error}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-white dark:bg-zinc-800">
            {captureRows.length === 0 ? (
              <div className="p-3 text-[11px] text-zinc-500 dark:text-zinc-400">
                Tap <strong>Start</strong>, then pull a stroke on the oarlock. Each notification
                from the 4 notifiable chars (configIndicate, dataStreamA, dataSummary, dataStreamB)
                appears below with timestamp + hex bytes + decoded int16-LE / float32-LE arrays.
                <br /><br />
                <strong>No frames after pulling a stroke?</strong> Tap <strong>Wake / Probe</strong> above —
                NK oarlocks often stay asleep until a paired SpeedCoach polls them, and the probe
                sends candidate wake bytes to the config-write char. Then try the stroke again.
                Use <strong>Copy</strong> to send the log to the clipboard or <strong>Download</strong> to save it as a .txt file in your Downloads folder.
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto divide-y divide-zinc-100 dark:divide-zinc-700" data-testid={`${vendor}-ble-capture-rows`}>
                {captureRows.map((row, i) => {
                  // Decode the bytes a few common ways
                  const dv = new DataView(row.bytes.buffer, row.bytes.byteOffset, row.bytes.byteLength);
                  const decodes = decodeBytesPreview(dv);
                  return (
                    <div key={`${row.ts}-${i}`} className="p-2 text-[10px] font-mono">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-zinc-500 dark:text-zinc-400">
                          #{row.idx} · {row.key} · {row.bytes.length}B
                        </span>
                        <span className="text-zinc-400 dark:text-zinc-500">+{i === captureRows.length - 1 ? 0 : (captureRows[0].ts - row.ts)}ms</span>
                      </div>
                      <div className="text-zinc-900 dark:text-white break-all">{row.hex || '(empty)'}</div>
                      {row.bytes.length >= 2 && (
                        <div className="text-zinc-500 dark:text-zinc-400 mt-0.5 leading-relaxed">
                          {decodes}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {tree && (
        <div className="rounded-lg border-2 border-zinc-200 dark:border-zinc-700 overflow-hidden" data-testid={`${vendor}-ble-tree`}>
          <div className="px-3 py-2 flex items-center justify-between bg-zinc-50 dark:bg-zinc-900/40 border-b border-zinc-200 dark:border-zinc-700">
            <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-700 dark:text-zinc-200">
              GATT Tree · {tree.services.length} svc / {tree.services.reduce((a, s) => a + s.characteristics.length, 0)} char
            </div>
            <button
              type="button"
              onClick={onCopyReport}
              data-testid={`${vendor}-ble-copy-btn`}
              className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
            >
              <Copy className="w-3 h-3" />
              Copy report
            </button>
          </div>
          <div className="p-3 space-y-3 max-h-72 overflow-y-auto bg-white dark:bg-zinc-800">
            {tree.services.map((svc, i) => (
              <div key={`${svc.uuid}-${i}`} className="space-y-1">
                <div className="font-mono text-[11px] font-bold text-zinc-900 dark:text-white break-all">
                  {svc.uuid}{svc.isPrimary && <span className="ml-2 text-[9px] text-zinc-500">PRIMARY</span>}
                </div>
                {svc.characteristicsError && (
                  <div className="ml-3 text-[10px] text-rose-600 dark:text-rose-400">
                    {svc.characteristicsError}
                  </div>
                )}
                <div className="ml-3 space-y-1">
                  {svc.characteristics.map((ch, j) => (
                    <div key={`${ch.uuid}-${j}`} className="flex flex-col gap-1 py-1 border-l-2 border-zinc-200 dark:border-zinc-700 pl-2">
                      <span className="font-mono text-[10px] text-zinc-700 dark:text-zinc-300 break-all">{ch.uuid}</span>
                      <PropertiesBadge props={ch.properties} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
